import { useState, useEffect } from 'react';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { queryClient } from '../../../application';
import toast from 'react-hot-toast';

import { Honeybadger } from '@honeybadger-io/react';
import TextInput from '../../inputs/form-inputs/TextInput';
import ImageCard from './_components/ImageCard';
import { getAuthToken, getImages, FIFTEEN_MINUTES } from '../lib';
import HoneybadgerWrapper from '../../honeybadger/HoneybadgerWrapper';

const ImagePreviewModal = ({ id, imagePreview }) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const handleImageOnLoad = (event) => {
    const { naturalWidth, naturalHeight } = event.target;
    setDimensions({ width: naturalWidth, height: naturalHeight });
  };

  return (
    <div className="modal fade" id={id} tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <div className="d-flex w-100 justify-content-between align-items-center">
              <div>
                <p className="modal-title">Previewing Image</p>
                <span className="text-muted">
                  (W: {dimensions.width}px x H: {dimensions.height}px)
                </span>
              </div>
              <div className="d-flex gap-2">
                <a className="btn btn-primary" href={imagePreview}>
                  <i className="fe fe-external-link" />
                </a>
                <button type="button" className="btn btn-danger" data-bs-dismiss="modal">
                  <i className="fe fe-x" />
                </button>
              </div>
            </div>
          </div>
          <div className="modal-body">
            <div className="media-preview large">
              <img src={imagePreview} onLoad={handleImageOnLoad} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function Images({ endpoints }) {
  const [search, setSearch] = useState('');
  const [filteredImages, setFilteredImages] = useState([]);
  const [imagePreview, setImagePreview] = useState('');

  const {
    data: authToken,
    isLoading: isTokenLoading,
    isError: isTokenError,
    error: tokenError,
  } = useQuery({
    queryKey: ['authToken'],
    queryFn: getAuthToken,
    staleTime: FIFTEEN_MINUTES,
    gcTime: FIFTEEN_MINUTES,
    refetchOnWindowFocus: false,
  });

  const {
    data: imagesData,
    isLoading: isImagesLoading,
    isError: isImagesError,
    error: imagesError,
  } = useQuery({
    queryKey: ['images', authToken],
    queryFn: () => getImages(authToken),
    enabled: !!authToken, // only run when token is available
  });

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const onDeleteImageCallback = (id) => {
    setFilteredImages((prevImages) => prevImages.filter((image) => image.id !== id));
    toast('Image deleted successfully!', { type: 'delete' });
  };

  useEffect(() => {
    const images = imagesData?.data ? [...imagesData.data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) : [];

    if (!search) {
      setFilteredImages(images);
      return;
    }

    const keyword = search.toLowerCase();
    const filtered = images.filter((image) => image.name.toLowerCase().includes(keyword));
    setFilteredImages(filtered);
  }, [search, imagesData]);

  useEffect(() => {
    if (isImagesError && imagesError) {
      Honeybadger.notify({
        name: imagesError.name,
        message: imagesError.message,
        stack: imagesError.stack,
      });
    }
    if (isTokenError && tokenError) {
      Honeybadger.notify({
        name: tokenError.name,
        message: tokenError.message,
        stack: tokenError.stack,
      });
    }
  }, [isImagesError, imagesError, isTokenError, tokenError]);

  const imagePreviewModalId = 'imagePreviewModal';

  return (
    <div className="image-manager">
      <TextInput placeholder="Search for an image..." onChange={handleSearch} style={{ maxWidth: 500 }} />
      <div className="tiles-of-images">
        {isTokenLoading || isImagesLoading ? (
          <div className="w-100 d-flex align-items-center justify-content-center p-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : isTokenError ? (
          <div className="w-100 d-flex align-items-center justify-content-center p-5">
            <div className="text-danger d-flex flex-column align-items-center gap-3">Error fetching auth token</div>
          </div>
        ) : isImagesError ? (
          <div className="w-100 d-flex align-items-center justify-content-center p-5">
            <div className="text-danger d-flex flex-column align-items-center gap-3">Error loading images</div>
          </div>
        ) : filteredImages.length ? (
          filteredImages.map((image) => (
            <ImageCard key={image.id} {...{ image, endpoints, setImagePreview, imagePreviewModalId, authToken, onDeleteImageCallback }} />
          ))
        ) : (
          <div className="w-100 d-flex align-items-center justify-content-center p-5">No images found</div>
        )}
      </div>
      <ImagePreviewModal {...{ id: imagePreviewModalId, imagePreview }} />
    </div>
  );
}

export default function IndexImages(props) {
  return (
    <HoneybadgerWrapper>
      <QueryClientProvider client={queryClient}>
        <Images {...props} />
      </QueryClientProvider>
    </HoneybadgerWrapper>
  );
}
